import { AlertTriangle, Ban, CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";

import { type AlertVariant, ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";

import { type PayoutBatch, PayoutBatchStatus } from "../../types/payoutBatches.types";

export interface BatchResultBannerProps {
  batch: PayoutBatch;
}

/** The verdict line above the progress surface: running, completed, completed with errors, failed, closed. */
export function BatchResultBanner({ batch }: BatchResultBannerProps) {
  const { t } = useTranslations("payoutBatches");

  const halted = batch.halt_code ? t("result_halted", { reason: batch.halt_code }) : null;

  switch (batch.status) {
    case PayoutBatchStatus.APPROVED:
    case PayoutBatchStatus.EXECUTING:
      return (
        <ColocaAlert
          variant="secondary"
          icon={Loader2}
          heading={t("result_executing_title")}
          text={t("result_executing_description")}
          className="[&>svg]:animate-spin"
        />
      );
    case PayoutBatchStatus.COMPLETED:
      return (
        <ColocaAlert
          variant="success"
          icon={CheckCircle2}
          heading={t("result_completed_title")}
          text={t("result_completed_description")}
        />
      );
    case PayoutBatchStatus.COMPLETED_WITH_ERRORS:
      return (
        <ColocaAlert
          variant="warning"
          icon={AlertTriangle}
          heading={t("result_completed_with_errors_title")}
          text={joinLines(t("result_completed_with_errors_description"), halted)}
        />
      );
    case PayoutBatchStatus.FAILED:
      return (
        <ColocaAlert
          variant="destructive"
          icon={XCircle}
          heading={t("result_failed_title")}
          text={joinLines(t("result_failed_description"), halted, batch.failure_reason)}
        />
      );
    case PayoutBatchStatus.CANCELLED:
      return (
        <ColocaAlert
          variant={"secondary" satisfies AlertVariant}
          icon={Ban}
          heading={t("result_cancelled_title")}
          text={joinLines(t("result_cancelled_description"), batch.cancel_reason)}
        />
      );
    case PayoutBatchStatus.EXPIRED:
      return (
        <ColocaAlert
          variant="warning"
          icon={Clock}
          heading={t("result_expired_title")}
          text={t("result_expired_description")}
        />
      );
    default:
      return null;
  }
}

function joinLines(...parts: Array<string | null | undefined>): string {
  return parts.filter((part): part is string => Boolean(part && part.trim())).join(" ");
}
